import React, { useRef, useEffect, useState, useContext } from 'react'
import useSize from './useSize'
import { scaleLinear } from 'd3-scale'
import { line, curveBasis } from 'd3-shape'
import {
  HorizontalTicks,
  VerticalTicks,
  AxisLabel,
  Curve
} from 'components/charts/ChartElements'

import './styles.scss'

const f = new Intl.NumberFormat('de-CH')

type ChartProps = {
  data: namedataElement[]
  selected: namedataElement[]
  colors: string[]
}
const yProp = 'pop'

export const Chart: React.FC<ChartProps> = ({ data, selected, colors }) => {
  const [yDomain, setYDomain] = useState<number[]>([0, 1])

  useEffect(() => {
    if (selected.length <= 0) return
    const max =
      Math.ceil(
        Math.max.apply(
          null,
          selected.map(s => {
            return Math.max.apply(
              null,
              s.years.map(y => y[yProp])
            )
          })
        ) / 0.2
      ) * 0.2
    setYDomain([0, max])
  }, [selected.length])

  const [container, size] = useSize()
  const width = size.width
  const height = size.isMobile ? 400 : 550
  const gutterTop = 20
  const gutterRight = 5
  const gutterLeft = 5
  const gutterBottom = 25

  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain([1925, 2021])
        .range([plotArea.left, plotArea.right]),
    [size, yDomain, selected]
  )
  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size, yDomain, selected]
  )

  const yFunction = (d: nameyearElement) => yAxisScale(d.pop)
  const xFunction = (d: nameyearElement) => xAxisScale(d.year)
  const [tooltipData, setTooltipData] = useState<tooltipdataData | undefined>()
  const svg = useRef<SVGSVGElement>(null)
  //

  const lineFunction = React.useMemo(
    () =>
      line<nameyearElement>()
        .x(xFunction)
        .y(yFunction)
        .curve(curveBasis),
    [size, yDomain, selected]
  )

  const yTicks = [0, yDomain[1] / 2, yDomain[1]]
  const xTicks = [1950, 1975, 2000]

  const prepareTooltipData = (
    year: number,
    selected: namedataElement[],
    y: number
  ) => {
    const names: tooltipdataElement[] = selected.map((s, i) => {
      const ret: tooltipdataElement = {
        name: s.name,
        gender: s.gender,
        color: colors[i],
        d: s.years.filter(f => f.year === year)[0],
        y: undefined
      }
      return ret
    })
    return { year, data: names, y: y } as tooltipdataData
  }
  const moveHandle = (e: React.MouseEvent) => {
    const year = Math.round(xAxisScale.invert(e.nativeEvent.offsetX))
    const curVals = selected.map(m => {
      return m.years.filter(f => f.year === year)[0].pop
    })
    setTooltipData(
      prepareTooltipData(year, selected, yAxisScale(Math.max(...curVals)) - 60)
    )
  }
  return (
    <div className='namechart'>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        {tooltipData && (
          <Tooltip
            data={tooltipData}
            x={xAxisScale(tooltipData.year)}
            width={width}
          />
        )}
        <svg
          id='namechart'
          className='namechart'
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
          onMouseMove={moveHandle}
          onMouseLeave={(e: React.MouseEvent) => {
            setTooltipData(undefined)
          }}
        >
          <rect
            x={gutterLeft}
            y={gutterTop}
            width={size.width - gutterLeft - gutterRight}
            height={height - gutterTop - gutterBottom}
            fill='var(--background-color-light)'
            pointerEvents='none'
          />
          <AxisLabel
            text='Anteil der Frauen/Männer mit diesem Namen'
            dir='up'
            pos={[0, yAxisScale.range()[1] - 20]}
          />
          <AxisLabel
            text='Jahrgang'
            dir='right'
            pos={[0, yAxisScale.range()[0] + 10]}
          />

          <HorizontalTicks
            values={yTicks}
            x={xAxisScale}
            y={yAxisScale}
            format={(m: number) => {
              return m.toFixed(1) + '%'
            }}
            textAnchor='start'
          />
          <VerticalTicks
            values={xTicks}
            x={xAxisScale}
            y={yAxisScale}
            format={(m: number) => {
              return m.toString()
            }}
          />
          {selected &&
            selected.map((m, i) => {
              return (
                <Curve
                  key={i}
                  d={
                    lineFunction(
                      m.years.filter(f => f.year >= xAxisScale.domain()[0])
                    ) || ''
                  }
                  color={colors[i]}
                />
              )
            })}

          {tooltipData && (
            <>
              <line
                x1={xAxisScale(tooltipData.year)}
                x2={xAxisScale(tooltipData.year)}
                y1={yAxisScale.range()[0]}
                y2={yAxisScale.range()[1]}
                className='tooltipline'
              />
              {tooltipData.data.map((d, i) => {
                return (
                  <>
                    {d.d && (
                      <circle
                        key={i}
                        cx={xAxisScale(tooltipData.year)}
                        cy={yAxisScale(d.d[yProp])}
                        r={10}
                        fill={colors[i]}
                        pointerEvents='none'
                      />
                    )}
                  </>
                )
              })}
            </>
          )}
        </svg>
      </div>
    </div>
  )
}

const Tooltip = ({
  data,
  x,
  width
}: {
  data: tooltipdataData
  x: number
  width: number
}) => {
  return (
    <div
      className={
        'tooltip ' + (x > width * 0.8 ? 'right' : x < width * 0.2 ? 'left' : '')
      }
      style={{
        left: (window.innerWidth < 600 ? 0 : x) + 'px',
        top: window.innerWidth < 600 ? data.y : data.y
      }}
    >
      {data.data.length > 0 && (
        <table>
          <tbody>
            <tr>
              <td className='year' colSpan={10}>
                Personen mit Jahrgang <br />
                <b>{data.year}</b>
              </td>
            </tr>

            {data.data
              .sort((a, b) => {
                if (a.d && b.d) return b.d.pop - a.d.pop
                return 0
              })
              .map((d, i) => {
                return (
                  <React.Fragment key={i}>
                    {d.d && (
                      <tr style={{ color: d.color }}>
                        <td>{d.name}</td>
                        <td className='value'>{d.d.pop}% </td>
                        <td className='label'>
                          {d.gender === 'w' ? 'der Frauen' : 'der Männer'}
                          {window.innerWidth < 600 ? ' (' + d.d.abs + ')' : ''}
                        </td>
                        {window.innerWidth > 600 && (
                          <>
                            <td className='value'>{d.d.abs} </td>
                            <td className='label'>
                              {d.gender === 'w' ? 'Frauen' : 'Männer'}
                            </td>
                          </>
                        )}
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
          </tbody>
        </table>
      )}
    </div>
  )
}

const sumProperty = (
  data: { [key: string]: any }[],
  returnFunction: (b: { [key: string]: any }) => number,
  precision = 1
) => {
  return data
    .reduce(
      (a: number, b: { [key: string]: any }) => a + returnFunction(b) || 0,
      0
    )
    .toFixed(precision)
}
